import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../../../themes/MultiSelect.css';
import { OptionItem, Value } from './components';
import { useComboboxValues } from './hooks/useValues';
import { ComboboxContext, useComboboxContext } from './context';
import { useComboboxClickAwayListener } from './hooks/useClickAwayListener';
import { Colors } from '../../../themes/colors';

export const Combobox = ({
  hasReachMax = false,
  isAutoApply = false,
  isError = false,
  isMultiple = false,
  isPaged = false,
  isReadonly = false,
  label,
  options = [],
  placeholder = 'Select',
  values = [],
  onChange,
  onNextPage = () => {},
  isDisabled = false,
}) => {
  const contextValue = useMemo(
    () => ({
      isAutoApply,
      isMultiple,
      options,
    }),
    [isAutoApply, isMultiple, options],
  );

  return (
    <ComboboxContext.Provider value={contextValue}>
      <Component
        hasReachMax={hasReachMax}
        isError={isError}
        isPaged={isPaged}
        isReadonly={isReadonly}
        label={label}
        placeholder={placeholder}
        values={values}
        onChange={onChange}
        onNextPage={onNextPage}
        isDisabled={isDisabled}
      />
    </ComboboxContext.Provider>
  );
};

const Component = ({
  hasReachMax,
  isError,
  isPaged,
  isReadonly,
  label,
  placeholder,
  values: defaultValues,
  onChange,
  onNextPage,
  isDisabled = false,
}) => {
  const [isOptionsVisible, setIsOptionsVisible] = useState(false);
  const optionsRef = useRef(null);
  const ulRef = useRef(null);

  useComboboxClickAwayListener((event) => {
    if (optionsRef.current && !optionsRef.current.contains(event.target)) {
      setIsOptionsVisible(false);
    }
  });

  const { isAutoApply, isMultiple, options } = useComboboxContext();
  const { isEmpty, handleValueChange, hasValue, values } = useComboboxValues(defaultValues);

  const handleSubmit = useCallback(
    (values) => {
      if (isMultiple) {
        onChange(values);
      } else {
        onChange(values[0]);
      }
    },
    [isMultiple, onChange],
  );

  const handleToggleOptions = useCallback(
    (event) => {
      if (isReadonly) {
        return;
      }

      if (Array.from(event.target.classList).includes('multiselect__apply')) {
        setIsOptionsVisible(false);
      } else {
        setIsOptionsVisible(true);
      }
    },
    [isReadonly],
  );

  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;

      const isAtBottom = scrollTop + clientHeight >= scrollHeight;

      if (hasReachMax || !isPaged || !isAtBottom) {
        return;
      }

      onNextPage();
    },
    [hasReachMax, isPaged, onNextPage],
  );

  useEffect(() => {
    if (!isOptionsVisible || !isPaged || hasReachMax) return;

    const { clientHeight, scrollHeight, scrollTop } = ulRef.current;

    const isAtBottom = scrollTop + clientHeight >= scrollHeight;

    if (!isAtBottom) {
      return;
    }

    onNextPage();
  }, [hasReachMax, isOptionsVisible, isPaged, onNextPage]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 200,
      }}
    >
      {label && <p>{label}</p>}
      <button
        className="multiselect custom-select custom-select-lg"
        type="button"
        onClick={handleToggleOptions}
        disabled={isDisabled}
        style={{
          height: 32,
          ...(isError
            ? {
                border: `1px solid ${Colors.error_400}`,
                backgroundColor: '#FEF9F9',
              }
            : {}),
        }}
      >
        <Value placeholder={placeholder} values={values} />

        {isOptionsVisible && (
          <div ref={optionsRef} className="multiselect__options_wrapper">
            <ul className="multiselect__options" ref={ulRef} onScroll={handleScroll}>
              {options.map((it) => (
                <OptionItem
                  isSelected={hasValue(it.value)}
                  key={it.value}
                  label={it.label}
                  value={it.value}
                  onClick={() => handleValueChange(it, isAutoApply ? handleSubmit : undefined)}
                />
              ))}
            </ul>
            {!isAutoApply && (
              <div className="multiselect__bottom_buttons">
                <button
                  disabled={isEmpty}
                  className="btn btn-info multiselect__apply"
                  type="button"
                  onClick={() => {
                    handleSubmit(values);
                    setIsOptionsVisible(false);
                  }}
                >
                  Apply
                </button>
              </div>
            )}
          </div>
        )}
      </button>
    </div>
  );
};
